import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  styled,
  TextField,
  Tooltip,
  tooltipClasses,
} from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import CRMCallAudioMessage from './message-types/CRMCallAudioMessage';
import CRMTaskMessage from './message-types/CRMTaskMessage';
// import CommentDatePicker from './comment-box-items/CommentDatePicker';
import CommentTypeSelect from './comment-box-items/CommentTypeSelect';
import CommentStaffSelect from './comment-box-items/CommentStaffSelect';
import useAuth from 'hooks/useAuth';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useNotification from 'hooks/useNotification';
import CRMLeadCommentMessage from './message-types/CRMLeadCommentMessage';
import CRMTasksModal from './CRMTasksModal';
import CRMDataChangeMessage from './message-types/CRMDataChangeMessage';
import usePermission from 'hooks/usePermission';
import CommentTaskTypesSelect from './comment-box-items/CommentTaskTypesSelect';
import { useAppInfo } from 'context';
import MDatePicker from 'components/ui/form/mDatePicker/MDatePicker';

const NoMaxWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
    padding: '2px',
  },
});

const CRMCardLeadHistory = ({ isUserNew = false }) => {
  const [{ refetchTaskCount }] = useAppInfo();
  const { t } = useTranslation();
  const { id } = useParams();
  const [{ user }] = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openTasks, setOpenTasks] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();
  const [selectedType, setSelectedType] = useState({});
  const [selectedStaff, setSelectedStaff] = useState(user?.user);
  const [selectedTaskType, setSelectedTaskType] = useState({});
  const [commentDate, setCommentDate] = useState(
    moment()?.format('YYYY-MM-DD HH:mm')
  );
  const [commentText, setCommentText] = useState('');
  const [leadHistory, setLeadHistory] = useState({});
  const [leadTasks, setLeadTasks] = useState([]);
  const { hasPermission } = usePermission();
  const [hasError, setHasError] = useState(false);
  const [mPickerVisible, setMPickerVisible] = useState(false);

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: ['/crm/crm-lead/history/{id}', id],
    queryFn: async function () {
      const response = await axiosPrivate.get(`/crm/crm-lead/history/${id}`);
      return response.data.data;
    },
    onSuccess: (data) => {
      if (data && data?.history) {
        setLeadHistory(sortHistoryData(data?.history));
        if (data && data?.lead && data?.lead?.tasks) {
          setLeadTasks(sortHistoryData({ ...data?.lead?.tasks }));
        }
      }
    },
    enabled: !hasError && !!id,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const sortHistoryData = (data) => {
    const uniqDay = Array.from(
      new Set(
        Object.values(data).map((item) =>
          moment(item?.created_at).format('DD_MM_YYYY')
        )
      )
    ).sort((a, b) => moment(a, 'DD_MM_YYYY').diff(moment(b, 'DD_MM_YYYY')));

    const sortedData = Object.keys(data)
      .map((item) => data[item])
      .sort((a, b) => moment(a?.created_at).diff(b?.created_at));
    let formedData = {};
    for (let i = 0; i < uniqDay.length; i++) {
      formedData[uniqDay[i]] = sortedData.filter(
        (sD) => moment(sD?.created_at).format('DD_MM_YYYY') == uniqDay[i]
        //  &&
        //   (!sD?.date || moment(sD?.date, 'YYYY-MM-DD').diff(moment()) <= 0)
      );
    }
    return formedData;
  };

  const sendComment = async () => {
    if (
      selectedType?.code &&
      selectedStaff?.id &&
      // commentText &&
      (selectedType?.code == 'TASK' ? moment(commentDate).isValid() : true)
    ) {
      try {
        setIsSubmitting(true);
        const response = await axiosPrivate.post(
          selectedType?.code == 'TASK' ? '/crm/task' : '/crm/lead-comment',
          selectedType?.code == 'TASK'
            ? JSON.stringify({
                lead_id: id,
                user_id: selectedStaff?.id,
                title: commentText,
                date: commentDate
                  ? moment(commentDate).format('YYYY-MM-DD HH:mm')
                  : null,
                type_id: selectedTaskType?.id,
              })
            : JSON.stringify({
                lead_id: id,
                user_id: selectedStaff?.id,
                comment: commentText,
              }),
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );
        if (response.data && response.data.status) {
          refetch();
          setCommentText('');
          setCommentDate(moment()?.format('YYYY-MM-DD HH:mm'));
          refetchTaskCount?.();
        }
        setIsSubmitting(false);
      } catch (error) {
        setIsSubmitting(false);
        sendNotification({
          msg: error?.response?.data?.message || error?.message,
          variant: 'error',
        });
      }
    } else {
      sendNotification({
        msg:
          selectedType?.code == 'TASK'
            ? "Iltimos, topshiriqni yaratish uchun kerakli maydonlarni to'ldiring!"
            : "Iltimos, xabarnomani yaratish uchun kerakli maydonlarni to'ldiring!",
        variant: 'warning',
      });
    }
  };

  const cancelSend = () => {
    setCommentText('');
  };

  const onChangeMPicker = (date, start, end) => {
    setCommentDate(date + 'T' + end ?? start);
    // setCreateDedline({
    //   start: date + ' ' + start,
    //   end: date + ' ' + end
    // });
  };

  const handleMPicker = () => {
    setMPickerVisible(true);
  };

  return (
    <div className='lead-history-items-wrapper'>
      {isLoading || isFetching ? (
        <div className='lead-history-details-wrapper'>
          <div className='circular-progress-box py-5'>
            <CircularProgress size={35} />
          </div>
        </div>
      ) : (
        <div
          className={`lead-history-details-wrapper${
            isUserNew ||
            !(hasPermission('CRM_ADD_TASK') || hasPermission('CRM_ADD_COMMENT'))
              ? ' is-new'
              : ''
          }`}
          id='lead-history-details-wrapper'
        >
          {leadHistory && Object.keys(leadHistory).length > 0 ? (
            Object.keys(leadHistory).map((historyItem, historyIndex) => (
              <div
                className='date-history-wrapper'
                key={`date-history-${historyIndex}`}
              >
                <div className='date-time-title'>
                  {moment(historyItem, 'DD_MM_YYYY').format('YYYY-MM-DD')}
                </div>
                <div className='date-items-wrapper'>
                  {leadHistory[historyItem] &&
                    leadHistory[historyItem].length > 0 &&
                    leadHistory[historyItem].map((item, itemIndex) => (
                      <div
                        className={`item-details-wrapper border-[1px] ${
                          item.type == 'task' &&
                          item?.status == 1 &&
                          moment(item?.date)?.isBefore()
                            ? 'border-red-600'
                            : 'border-white'
                        }`}
                        key={`item-details-${itemIndex}`}
                      >
                        {item.type == 'call_history' && (
                          <CRMCallAudioMessage data={item} />
                        )}
                        {item.type == 'task' && (
                          <CRMTaskMessage data={item} refetchFn={refetch} />
                        )}
                        {item.type == 'lead_comment' && (
                          <CRMLeadCommentMessage data={item} />
                        )}
                        {item.type == 'lead_history' && (
                          <CRMDataChangeMessage data={item} />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))
          ) : (
            <div className='no-data-placeholder'>
              <span className='no-data-found-wrapper bg-transparent !text-base-color-light p-4 rounded-lg'>
                <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                {t('common.global.noDataFound')}
              </span>
            </div>
          )}
        </div>
      )}

      {!isUserNew &&
        (hasPermission('CRM_ADD_TASK') || hasPermission('CRM_ADD_COMMENT')) && (
          <div className='lead-history-input-wrapper'>
            <div className='input-body'>
              <div className='flex items-center justify-between'>
                <div className='input-top-actions-wrapper'>
                  <CommentTypeSelect
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                  />
                  <div className='divider'></div>
                  {/* {selectedType?.code == 'TASK' && (
                    <Fragment>
                      <CommentDatePicker
                        commentDate={commentDate}
                        setCommentDate={setCommentDate}
                      />
                      <div className='divider'></div>
                    </Fragment>
                  )} */}
                  {/* {selectedType?.code == 'TASK' && (
                    <Fragment>
                      <MDatePicker
                        commentDate={commentDate}
                        setCommentDate={setCommentDate}
                        visibleTime={true}
                        onChangeMPicker={(...e) => {
                          console.log(e);
                        }}
                      />
                      <div className='divider'></div>
                    </Fragment>
                  )} */}

                  {selectedType?.code == 'TASK' && (
                    <>
                      <ClickAwayListener
                        onClickAway={() => setMPickerVisible(false)}
                      >
                        <div>
                          <NoMaxWidthTooltip
                            title={
                              <MDatePicker
                                commentDate={commentDate}
                                setCommentDate={setCommentDate}
                                visibleTime={true}
                                onChangeMPicker={onChangeMPicker}
                              />
                            }
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={() => setMPickerVisible(false)}
                            open={mPickerVisible}
                            placement='top'
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                          >
                            <TextField
                              className={'!w-[140px] !h-[30px]'}
                              onClick={handleMPicker}
                              value={
                                commentDate
                                  ? moment(commentDate)?.format(
                                      'DD.MM.YYYY HH:mm'
                                    )
                                  : ''
                              }
                              sx={{
                                '.MuiInputBase-root': {
                                  height: 30,
                                },
                              }}
                            />
                          </NoMaxWidthTooltip>
                        </div>
                      </ClickAwayListener>
                      <div className='divider'></div>
                    </>
                  )}

                  <CommentStaffSelect
                    selectedStaff={selectedStaff}
                    setSelectedStaff={setSelectedStaff}
                  />

                  {selectedType?.code == 'TASK' && (
                    <Fragment>
                      <div className='divider'></div>
                      <CommentTaskTypesSelect
                        selectedTaskType={selectedTaskType}
                        setSelectedTaskType={setSelectedTaskType}
                      />
                    </Fragment>
                  )}
                </div>
              </div>

              <div className='input-text-area'>
                <TextField
                  color='formColor'
                  variant='outlined'
                  fullWidth
                  id='comment-text-area-field'
                  name='comment-text-area-field'
                  className='comment-text-area-field'
                  label=''
                  multiline={true}
                  disabled={isLoading || isFetching}
                  rows={3}
                  value={commentText}
                  onChange={(event) => {
                    setCommentText(event.target.value);
                  }}
                  autoComplete='off'
                />
                {!commentText && (
                  <span className='comment-text-area-field-placeholder'>
                    Matnni kiriting...
                  </span>
                )}
              </div>
              <div className='input-action-buttons-wrapper'>
                <Button
                  color='success'
                  variant='contained'
                  size='small'
                  className='!mr-2'
                  onClick={() => sendComment()}
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress
                      size={15}
                      color='inherit'
                      className='mr-1'
                    />
                  )}
                  Yuborish
                </Button>
                <Button
                  variant='contained'
                  color='inherit'
                  size='small'
                  onClick={() => cancelSend()}
                  disabled={isSubmitting}
                >
                  {t('common.button.cancel')}
                </Button>
              </div>
            </div>
          </div>
        )}

      {openTasks && (
        <CRMTasksModal
          open={openTasks}
          setOpen={setOpenTasks}
          tasks={leadTasks}
          refetchFn={refetch}
        />
      )}
    </div>
  );
};
export default CRMCardLeadHistory;
